import type { Channel } from 'phoenix'

/**
 * Social-channel construction, kept apart from the socket store so the
 * bind-before-join ordering is structural rather than a convention.
 *
 * WHY THIS EXISTS. The socket-server queues `:after_social_join` from inside the
 * join handler (`realtime_channel.ex`), so `round:snapshot` — the round's whole
 * pre-join bet history — arrives immediately after the join reply. phoenix-js
 * discards events that have no binding, and nothing ever re-requests the
 * snapshot: `push_social_bets_snapshot` has exactly one call site (the join) and
 * the widget never pushes on the social channel.
 *
 * Binding from a React effect that awaits the join promise is one commit too
 * late. Anyone who opens the game, switches games, refreshes or reconnects
 * mid-round therefore started with an empty feed and only saw bets placed from
 * that moment on. Building the channel through `createBoundChannel` makes it
 * impossible to join before the listeners exist.
 */

/** The one place the social topic string is built. */
export const socialTopicFor = (gameId: string): string =>
  `realtime:public:game-${gameId}:social-events`

type ChannelFactory = { channel: (topic: string) => Channel }

/** Registers listeners on a freshly created channel, before it is joined. */
export type SocialBinder = (channel: Channel) => void

/**
 * Create the channel and bind its listeners. The caller joins AFTERWARDS —
 * never inside this function, so the ordering reads at the call site.
 *
 * A throwing binder costs its own listener, not the channel: losing the feed
 * entirely because one `on()` failed would be a worse failure than the one this
 * module exists to prevent.
 */
export const createBoundChannel = (
  socket: ChannelFactory,
  topic: string,
  bind: SocialBinder | undefined
): Channel => {
  const channel = socket.channel(topic)

  if (bind) {
    try {
      bind(channel)
    } catch (error) {
      console.error('[SOCIAL CHANNEL] Listener binding failed:', error)
    }
  }

  return channel
}

/**
 * The last binder handed in by the React layer.
 *
 * `rejoinAllChannels` re-joins from inside the socket store, where the
 * component-owned handlers are not in scope. Without this, every reconnect
 * would build a channel with NO listeners — strictly worse than the race the
 * pre-binding closes. Module-scoped rather than store state because it is a
 * function reference, not something any view renders.
 */
let socialBinder: SocialBinder | undefined

export const rememberSocialBinder = (binder: SocialBinder): void => {
  socialBinder = binder
}

/** Read the remembered binder. Deliberately non-consuming — a reconnect may re-bind many times. */
export const takeSocialBinder = (): SocialBinder | undefined => socialBinder

export const clearSocialBinder = (): void => {
  socialBinder = undefined
}

/**
 * Whether a `phx_close` should release the store's social channel.
 *
 * phoenix-js treats `phx_close` as TERMINAL for a Channel — `onClose` does
 * `rejoinTimer.reset()` + `socket.remove(this)`, so unlike an *errored* channel
 * (which self-rejoins) a *closed* one never comes back. The widget attaches no
 * channel-level hooks, so a closed social channel simply went quiet: the client
 * kept rendering, kept its socket, and silently stopped receiving other
 * players' bets. The only thing that revived it was an unrelated re-render
 * re-arming the join effect, which is why the feed appeared to work better
 * while the viewer was betting — betting drives rAF tweens, hence renders.
 *
 * Clearing the store entry flips the derived join guard and re-fires the join
 * effect on the next commit, independent of render frequency.
 *
 * The identity check is load-bearing: a close arriving while we are ALREADY
 * swapping channels (game switch, or `joinSocialChannel`'s own leave-then-join)
 * must not wipe the newer channel the store has since accepted.
 */
export const shouldReleaseOnClose = (
  storedChannel: Channel | undefined,
  closingChannel: Channel
): boolean => storedChannel === closingChannel

/**
 * Single-flight guard for the social join.
 *
 * Releasing the channel on close re-fires the join effect, and `onOpen →
 * rejoinAllChannels` fires its own join — so without this the two can overlap
 * and issue two `phx_join` for one topic. The server closes the duplicate,
 * which triggers another release, which starts another join: a rejoin loop
 * built out of two individually correct recovery paths.
 *
 * Keyed by game rather than a bare boolean so switching games is never blocked
 * by a join still settling for the game being left.
 */
let joinInFlightFor: string | undefined

/** Returns false when a join for this game is already running — caller should bail. */
export const beginSocialJoin = (gameId: string): boolean => {
  if (joinInFlightFor === gameId) return false
  joinInFlightFor = gameId
  return true
}

/**
 * Release the slot — but ONLY if this game still owns it.
 *
 * The ownership check is load-bearing. A game switch is allowed to claim the
 * slot while the previous game's join is still settling (that join can burn
 * ~3s leaving plus up to 7s joining on a degraded socket). The older promise
 * then settles FIRST and, unkeyed, would clear the newer game's lock while its
 * join is still running — re-opening exactly the concurrent-join window the
 * guard exists to close.
 */
export const endSocialJoin = (gameId?: string): void => {
  if (gameId !== undefined && joinInFlightFor !== gameId) return
  joinInFlightFor = undefined
}
